import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    createWarehouse,
    getWarehouses,
    addWarehouseOperation,
    getWarehouseOperations,
} from "../../api/warehouseService";

import {
    PageHeader,
    Panel,
    KpiCard,
} from "../../components/ui/AdminUI";

import DataTable from "../../components/ui/DataTable";
import StatusBadge from "../../components/ui/StatusBadge";

import {
    TextInput,
    NumberInput,
    SelectBox,
    PrimaryButton,
    SuccessButton,
    SecondaryButton,
} from "../../components/ui/FormControls";

const EMPTY_WAREHOUSE_FORM = {
    name: "",
    addressId: "",
    managerId: "",
    capacity: "",
};

const EMPTY_OPERATION_FORM = {
    shipmentId: "",
    parcelId: "",
    operationType: "Received",
    notes: "",
};

const OPERATION_TYPES = [
    "Received",
    "Stored",
    "Released",
    "Transferred",
    "Returned",
    "Inspected",
];

export default function WarehousePage() {
    const [warehouses, setWarehouses] = useState([]);
    const [operations, setOperations] = useState([]);

    const [selectedWarehouseId, setSelectedWarehouseId] =
        useState("");

    const [form, setForm] = useState(
        EMPTY_WAREHOUSE_FORM
    );

    const [operation, setOperation] = useState(
        EMPTY_OPERATION_FORM
    );

    const [loadingWarehouses, setLoadingWarehouses] =
        useState(true);

    const [loadingOperations, setLoadingOperations] =
        useState(false);

    const [creating, setCreating] = useState(false);
    const [addingOperation, setAddingOperation] =
        useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const getErrorMessage = (
        err,
        fallbackMessage
    ) => {
        const validationErrors =
            err?.response?.data?.errors;

        if (validationErrors) {
            const messages = Object.values(
                validationErrors
            )
                .flat()
                .filter(Boolean);

            if (messages.length > 0) {
                return messages.join(" ");
            }
        }

        return (
            err?.response?.data?.message ||
            err?.message ||
            fallbackMessage
        );
    };

    const loadWarehouses = useCallback(async () => {
        try {
            setLoadingWarehouses(true);
            setError("");

            const data = await getWarehouses();
            const list = Array.isArray(data)
                ? data
                : [];

            setWarehouses(list);

            setSelectedWarehouseId((current) => {
                const currentStillExists =
                    list.some(
                        (warehouse) =>
                            String(warehouse.id) ===
                            String(current)
                    );

                if (current && currentStillExists) {
                    return current;
                }

                return list[0]?.id
                    ? String(list[0].id)
                    : "";
            });
        } catch (err) {
            console.error(
                "Failed to load warehouses:",
                err
            );

            setError(
                getErrorMessage(
                    err,
                    "Failed to load warehouses."
                )
            );
        } finally {
            setLoadingWarehouses(false);
        }
    }, []);

    const loadOperations = useCallback(
        async (
            warehouseId = selectedWarehouseId
        ) => {
            const numericWarehouseId =
                Number(warehouseId);

            if (
                !Number.isInteger(
                    numericWarehouseId
                ) ||
                numericWarehouseId <= 0
            ) {
                setOperations([]);
                return;
            }

            try {
                setLoadingOperations(true);
                setError("");

                const data =
                    await getWarehouseOperations(
                        numericWarehouseId
                    );

                setOperations(
                    Array.isArray(data)
                        ? data
                        : []
                );
            } catch (err) {
                console.error(
                    "Failed to load warehouse operations:",
                    err
                );

                setOperations([]);

                setError(
                    getErrorMessage(
                        err,
                        "Failed to load warehouse operations."
                    )
                );
            } finally {
                setLoadingOperations(false);
            }
        },
        [selectedWarehouseId]
    );

    useEffect(() => {
        loadWarehouses();
    }, [loadWarehouses]);

    useEffect(() => {
        if (!selectedWarehouseId) {
            setOperations([]);
            return;
        }

        loadOperations(selectedWarehouseId);
    }, [
        selectedWarehouseId,
        loadOperations,
    ]);

    const handleWarehouseFieldChange = (
        field,
        value
    ) => {
        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleOperationFieldChange = (
        field,
        value
    ) => {
        setOperation((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleCreate = async (event) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        const name = form.name.trim();
        const capacity = Number(form.capacity);

        if (!name) {
            setError(
                "Warehouse name is required."
            );
            return;
        }

        if (name.length > 150) {
            setError(
                "Warehouse name cannot exceed 150 characters."
            );
            return;
        }

        if (
            !Number.isInteger(capacity) ||
            capacity <= 0
        ) {
            setError(
                "Warehouse capacity must be a positive integer."
            );
            return;
        }

        const addressId = form.addressId
            ? Number(form.addressId)
            : null;

        const managerId = form.managerId
            ? Number(form.managerId)
            : null;

        if (
            addressId !== null &&
            (!Number.isInteger(addressId) ||
                addressId <= 0)
        ) {
            setError(
                "Address ID must be a positive integer."
            );
            return;
        }

        if (
            managerId !== null &&
            (!Number.isInteger(managerId) ||
                managerId <= 0)
        ) {
            setError(
                "Manager ID must be a positive integer."
            );
            return;
        }

        try {
            setCreating(true);

            const createdWarehouse =
                await createWarehouse({
                    name,
                    addressId,
                    managerId,
                    capacity,
                });

            setForm(EMPTY_WAREHOUSE_FORM);

            setSuccess(
                "Warehouse created successfully."
            );

            await loadWarehouses();

            if (createdWarehouse?.id) {
                setSelectedWarehouseId(
                    String(createdWarehouse.id)
                );
            }
        } catch (err) {
            console.error(
                "Failed to create warehouse:",
                err
            );

            setError(
                getErrorMessage(
                    err,
                    "Failed to create warehouse."
                )
            );
        } finally {
            setCreating(false);
        }
    };

    const handleAddOperation = async (
        event
    ) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        const warehouseId = Number(
            selectedWarehouseId
        );

        if (
            !Number.isInteger(warehouseId) ||
            warehouseId <= 0
        ) {
            setError(
                "Warehouse is required."
            );
            return;
        }

        const shipmentId =
            operation.shipmentId === ""
                ? null
                : Number(
                    operation.shipmentId
                );

        const parcelId =
            operation.parcelId === ""
                ? null
                : Number(operation.parcelId);

        if (
            shipmentId === null &&
            parcelId === null
        ) {
            setError(
                "Shipment ID or Parcel ID is required."
            );
            return;
        }

        if (
            shipmentId !== null &&
            (!Number.isInteger(shipmentId) ||
                shipmentId <= 0)
        ) {
            setError(
                "Shipment ID must be a positive integer."
            );
            return;
        }

        if (
            parcelId !== null &&
            (!Number.isInteger(parcelId) ||
                parcelId <= 0)
        ) {
            setError(
                "Parcel ID must be a positive integer."
            );
            return;
        }

        const operationType =
            operation.operationType.trim();

        if (!operationType) {
            setError(
                "Operation type is required."
            );
            return;
        }

        if (operationType.length > 50) {
            setError(
                "Operation type cannot exceed 50 characters."
            );
            return;
        }

        const notes =
            operation.notes.trim() || null;

        if (
            notes !== null &&
            notes.length > 500
        ) {
            setError(
                "Notes cannot exceed 500 characters."
            );
            return;
        }

        try {
            setAddingOperation(true);

            await addWarehouseOperation(
                warehouseId,
                {
                    shipmentId,
                    parcelId,
                    operationType,
                    notes,
                }
            );

            setOperation(
                EMPTY_OPERATION_FORM
            );

            setSuccess(
                "Warehouse operation added successfully."
            );

            await loadOperations(
                warehouseId
            );
        } catch (err) {
            console.error(
                "Failed to add warehouse operation:",
                err
            );

            setError(
                getErrorMessage(
                    err,
                    "Failed to add warehouse operation."
                )
            );
        } finally {
            setAddingOperation(false);
        }
    };

    const selectedWarehouse =
        useMemo(
            () =>
                warehouses.find(
                    (warehouse) =>
                        String(
                            warehouse.id
                        ) ===
                        String(
                            selectedWarehouseId
                        )
                ) || null,
            [
                warehouses,
                selectedWarehouseId,
            ]
        );

    const stats = useMemo(() => {
        const activeWarehouses =
            warehouses.filter(
                (warehouse) =>
                    warehouse.isActive ===
                    true
            ).length;

        const totalCapacity =
            warehouses.reduce(
                (sum, warehouse) =>
                    sum +
                    Number(
                        warehouse.capacity ||
                        0
                    ),
                0
            );

        return {
            total: warehouses.length,
            active: activeWarehouses,
            capacity: totalCapacity,
            operations: operations.length,
        };
    }, [warehouses, operations]);

    const warehouseColumns = [
        {
            key: "id",
            label: "ID",
        },
        {
            key: "name",
            label: "Warehouse",
            render: (row) => (
                <span className="font-bold text-slate-900">
                    {row.name || "�"}
                </span>
            ),
        },
        {
            key: "addressId",
            label: "Address",
            render: (row) =>
                formatAddress(row),
        },
        {
            key: "managerId",
            label: "Manager",
            render: (row) =>
                formatManager(row),
        },
        {
            key: "capacity",
            label: "Capacity",
            render: (row) =>
                Number.isFinite(
                    Number(row.capacity)
                )
                    ? Number(
                        row.capacity
                    ).toLocaleString()
                    : "�",
        },
        {
            key: "isActive",
            label: "Status",
            render: (row) => (
                <StatusBadge
                    status={
                        row.isActive
                            ? "Active"
                            : "Inactive"
                    }
                />
            ),
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => {
                const isSelected =
                    String(row.id) ===
                    String(
                        selectedWarehouseId
                    );

                return (
                    <PrimaryButton
                        type="button"
                        className="px-3 py-2"
                        disabled={isSelected}
                        onClick={() =>
                            setSelectedWarehouseId(
                                String(row.id)
                            )
                        }
                    >
                        {isSelected
                            ? "Selected"
                            : "Select"}
                    </PrimaryButton>
                );
            },
        },
    ];

    const operationColumns = [
        {
            key: "id",
            label: "ID",
        },
        {
            key: "shipmentId",
            label: "Shipment",
            render: (row) =>
                row.shipmentId ?? "�",
        },
        {
            key: "parcelId",
            label: "Parcel",
            render: (row) =>
                row.parcelId ?? "�",
        },
        {
            key: "operationType",
            label: "Operation",
            render: (row) => (
                <StatusBadge
                    status={
                        row.operationType ||
                        "Unknown"
                    }
                />
            ),
        },
        {
            key: "processedByUserId",
            label: "Processed By",
            render: (row) =>
                formatProcessedBy(row),
        },
        {
            key: "notes",
            label: "Notes",
            render: (row) =>
                row.notes || "�",
        },
        {
            key: "operationDate",
            label: "Date",
            render: (row) =>
                formatDateTime(
                    row.operationDate
                ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Warehouse Management"
                subtitle="Manage warehouses, storage capacity and shipment operations."
                action={
                    <SecondaryButton
                        type="button"
                        onClick={
                            loadWarehouses
                        }
                        disabled={
                            loadingWarehouses
                        }
                    >
                        {loadingWarehouses
                            ? "Refreshing..."
                            : "Refresh Warehouses"}
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    title="Warehouses"
                    value={stats.total}
                    color="blue"
                />

                <KpiCard
                    title="Active Warehouses"
                    value={stats.active}
                    color="green"
                />

                <KpiCard
                    title="Total Capacity"
                    value={stats.capacity.toLocaleString()}
                    color="purple"
                />

                <KpiCard
                    title="Loaded Operations"
                    value={stats.operations}
                    color="teal"
                />
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Panel
                    title="Create Warehouse"
                    subtitle="Register a new warehouse facility."
                >
                    <form
                        onSubmit={
                            handleCreate
                        }
                        className="space-y-4"
                    >
                        <TextInput
                            label="Warehouse Name"
                            value={form.name}
                            onChange={(event) =>
                                handleWarehouseFieldChange(
                                    "name",
                                    event.target
                                        .value
                                )
                            }
                            placeholder="Warehouse name"
                            maxLength={150}
                            required
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                            <NumberInput
                                label="Address ID"
                                value={
                                    form.addressId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleWarehouseFieldChange(
                                        "addressId",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Optional address ID"
                                min="1"
                                step="1"
                            />

                            <NumberInput
                                label="Manager User ID"
                                value={
                                    form.managerId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleWarehouseFieldChange(
                                        "managerId",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Optional manager ID"
                                min="1"
                                step="1"
                            />
                        </div>

                        <NumberInput
                            label="Capacity"
                            value={
                                form.capacity
                            }
                            onChange={(event) =>
                                handleWarehouseFieldChange(
                                    "capacity",
                                    event.target
                                        .value
                                )
                            }
                            placeholder="Warehouse capacity"
                            min="1"
                            step="1"
                            required
                        />

                        <SuccessButton
                            type="submit"
                            disabled={creating}
                        >
                            {creating
                                ? "Creating..."
                                : "Create Warehouse"}
                        </SuccessButton>
                    </form>
                </Panel>

                <Panel
                    title="Warehouse Operation"
                    subtitle="Record shipment or parcel activity inside a warehouse."
                >
                    <form
                        onSubmit={
                            handleAddOperation
                        }
                        className="space-y-4"
                    >
                        <SelectBox
                            label="Warehouse"
                            value={
                                selectedWarehouseId
                            }
                            onChange={(event) =>
                                setSelectedWarehouseId(
                                    event.target
                                        .value
                                )
                            }
                            required
                        >
                            <option value="">
                                Select warehouse
                            </option>

                            {warehouses.map(
                                (warehouse) => (
                                    <option
                                        key={
                                            warehouse.id
                                        }
                                        value={
                                            warehouse.id
                                        }
                                    >
                                        {warehouse.name ||
                                            `Warehouse #${warehouse.id}`}
                                    </option>
                                )
                            )}
                        </SelectBox>

                        {selectedWarehouse && (
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                <div>
                                    <strong>
                                        Selected:
                                    </strong>{" "}
                                    {
                                        selectedWarehouse.name
                                    }
                                </div>

                                <div>
                                    <strong>
                                        Capacity:
                                    </strong>{" "}
                                    {Number(
                                        selectedWarehouse.capacity ||
                                        0
                                    ).toLocaleString()}
                                </div>
                            </div>
                        )}

                        <div className="grid gap-4 md:grid-cols-2">
                            <NumberInput
                                label="Shipment ID"
                                value={
                                    operation.shipmentId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleOperationFieldChange(
                                        "shipmentId",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Shipment ID"
                                min="1"
                                step="1"
                            />

                            <NumberInput
                                label="Parcel ID"
                                value={
                                    operation.parcelId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleOperationFieldChange(
                                        "parcelId",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Parcel ID"
                                min="1"
                                step="1"
                            />
                        </div>

                        <SelectBox
                            label="Operation Type"
                            value={
                                operation.operationType
                            }
                            onChange={(event) =>
                                handleOperationFieldChange(
                                    "operationType",
                                    event.target
                                        .value
                                )
                            }
                            required
                        >
                            {OPERATION_TYPES.map(
                                (
                                    operationType
                                ) => (
                                    <option
                                        key={
                                            operationType
                                        }
                                        value={
                                            operationType
                                        }
                                    >
                                        {
                                            operationType
                                        }
                                    </option>
                                )
                            )}
                        </SelectBox>

                        <TextInput
                            label="Notes"
                            value={
                                operation.notes
                            }
                            onChange={(event) =>
                                handleOperationFieldChange(
                                    "notes",
                                    event.target
                                        .value
                                )
                            }
                            placeholder="Operation notes"
                            maxLength={500}
                        />

                        <div className="flex flex-wrap gap-3">
                            <PrimaryButton
                                type="submit"
                                disabled={
                                    addingOperation ||
                                    !selectedWarehouseId
                                }
                            >
                                {addingOperation
                                    ? "Adding..."
                                    : "Add Operation"}
                            </PrimaryButton>

                            <SecondaryButton
                                type="button"
                                onClick={() =>
                                    loadOperations()
                                }
                                disabled={
                                    loadingOperations ||
                                    !selectedWarehouseId
                                }
                            >
                                {loadingOperations
                                    ? "Loading..."
                                    : "Refresh Operations"}
                            </SecondaryButton>
                        </div>
                    </form>
                </Panel>
            </div>

            <Panel
                title="Warehouses"
                subtitle="Registered warehouse facilities."
            >
                {loadingWarehouses ? (
                    <p className="text-slate-500">
                        Loading warehouses...
                    </p>
                ) : (
                    <DataTable
                        columns={
                            warehouseColumns
                        }
                        rows={warehouses}
                        rowKey="id"
                        emptyText="No warehouses found."
                    />
                )}
            </Panel>

            <Panel
                title="Warehouse Operations"
                subtitle={
                    selectedWarehouse
                        ? `Operations for ${selectedWarehouse.name}.`
                        : "Select a warehouse to load operations."
                }
            >
                {loadingOperations ? (
                    <p className="text-slate-500">
                        Loading warehouse operations...
                    </p>
                ) : (
                    <DataTable
                        columns={
                            operationColumns
                        }
                        rows={operations}
                        rowKey="id"
                        emptyText={
                            selectedWarehouseId
                                ? "No warehouse operations found."
                                : "Select a warehouse first."
                        }
                    />
                )}
            </Panel>
        </div>
    );
}

function formatAddress(warehouse) {
    const address = warehouse?.address;

    if (!address) {
        return warehouse?.addressId
            ? `Address #${warehouse.addressId}`
            : "�";
    }

    const parts = [
        address.addressLine,
        address.street,
        address.houseNumber,
        address.city?.name,
        address.postalCode?.code,
        address.country?.name,
    ].filter(Boolean);

    return (
        address.fullAddress ||
        parts.join(", ") ||
        `Address #${warehouse.addressId}`
    );
}

function formatManager(warehouse) {
    const manager = warehouse?.manager;

    if (!manager) {
        return warehouse?.managerId
            ? `User #${warehouse.managerId}`
            : "�";
    }

    const fullName = [
        manager.name,
        manager.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        fullName ||
        manager.userName ||
        manager.username ||
        manager.email ||
        `User #${warehouse.managerId}`
    );
}

function formatProcessedBy(operation) {
    const user = operation?.processedByUser;

    if (!user) {
        return operation?.processedByUserId
            ? `User #${operation.processedByUserId}`
            : "System";
    }

    const fullName = [
        user.name,
        user.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        fullName ||
        user.userName ||
        user.username ||
        user.email ||
        `User #${operation.processedByUserId}`
    );
}

function formatDateTime(value) {
    if (!value) {
        return "�";
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? "�"
        : date.toLocaleString();
}